<template>
    <div class="products-box">
      <!-- 筛选 -->
      <div class="filter-section">
        <div class="filter-btn" @click="drawerVisible = true">
          <span>{{ $t('products.screening') }}</span>
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>

      <!-- 分类弹层 -->
      <el-drawer
          title=""
          :visible.sync="drawerVisible"
          direction="ttb"
          size="80%"
          custom-class="filter-drawer"
      >
        <div class="filter-drawer-content">
          <div class="category-group" v-for="category in categories" :key="category.id">
            <h3 class="category-title">{{ category.name }}</h3>
            <ul class="category-list" v-if="category.children && category.children.length">
              <li
                  v-for="item in category.children"
                  :key="item.id"
                  :class="{ active: menuId === item.id }"
                  @click="handleDrawerCategoryClick(item)"
              >
                <span>{{ item.name }}</span>
              </li>
            </ul>
          </div>
        </div>
      </el-drawer>

      <section class="products-section section section--white">
        <div class="container">
          <div class="products-layout flex">
            <!-- 右侧产品列表 -->
            <div class="products-container" v-loading="loading" element-loading-text="加载中...">
              <div class="products-grid">
                <div v-for="product in filteredProducts"
                     :key="product.id"
                     class="product-card card hover-lift"
                     @click="handleProductClick(product)">
                  <div class="product-image">
                    <img v-lazy="product.cover+ '?x-oss-process=image/resize,w_196'" :alt="product.name" class="img-cover">
                  </div>
                  <div class="product-content">
                    <h3 class="product-name">{{ product.name }}</h3>
                    <p class="product-chemical-name text-ellipsis-multiple">{{ product.nameOther || '--' }}</p>
                    <div class="product-info">
                      <span class="product-cas">{{ $t('products.panel.CAS') }}: {{ product.casNo }}</span>
                      <span class="product-sn">{{ $t('products.panel.ArtNo') }}: {{ product.sn }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 无数据提示 -->
              <div v-if="!loading && filteredProducts.length === 0" class="no-data">
                暂无相关产品
              </div>

            </div>
          </div>

        </div>
        <!-- 分页器 -->
        <div v-if="filteredProducts.length > 0" class="pagination-section">
          <div class="pagination-wrapper container">
            <Pagination
                :total="total"
                :page-size="pageSize"
                :current-page="currentPage"
                @current-change="handlePageChange"
            />
          </div>
        </div>
      </section>
    </div>
</template>

<script>
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { getProductCenterList } from '@/api/products'
import {mapState} from "vuex";
import Pagination from '@/components/Pagination.vue'
import {pushWithLanguage} from "@/utils/routeUtils";

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'ProductsList',
  components: {
    Pagination
  },

  data() {
    return {
      drawerVisible: false,
      bannerBg: 'https://file.yacoo.com.cn/staticResource/products/products-header-bg.webp?x-oss-process=image/resize,h_480',
      searchKeyword: '',
      filteredProducts: [],
      categories: [], // 所有分类数据
      menuId: '',
      categoryInfo: null,
      pageSize: 10,
      loading: false
    }
  },

  props: {
    products: {
      type: Array,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    selectedCategory: {
      type: String,
      default: ''
    },
    currentPage: {
      type: Number,
      default: 1
    }
  },

  computed: {
    ...mapState({
      currentLanguage: state => state.app.language
    })
  },

  watch: {
    selectedCategory: {
      handler(newVal, oldVal) {
        // 监听分类变化
        this.menuId = newVal
      },
      immediate: true
    },
    products: {
      handler(newVal, oldVal) {
        this.filteredProducts = newVal
        this.initAnimations()
      },
      immediate: true
    },
    currentLanguage: {
      handler() {
        this.getCategories()
      }
    }
  },

  created() {
  },

  mounted() {
    this.getCategories ()
  },

  methods: {
    async getCategories() {
      try {
        const response = await getProductCenterList()
        if (response.success) {
          this.categories = response.data
        } else {
          this.$message.error(response.msg || '获取产品分类失败')
        }
      } catch (error) {
        console.error('获取产品分类失败:', error)
        this.$message.error('获取产品分类失败，请稍后重试')
      }
    },

    handleDrawerCategoryClick(item) {
      this.menuId = item.id
      this.drawerVisible = false
      //传递给父组件
      this.$emit('getSelectedCategory', this.menuId)
    },

    handleProductClick(product) {
      pushWithLanguage(`/products/${product.id}` , this.currentLanguage).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },

    handlePageChange(page) {
      //传递给父组件
      this.$emit('pageChange', page)
    },

    initAnimations() {
      // 产品卡片动画
      this.$nextTick(() => {
        // 使用 timeline 控制动画序列
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: '.products-grid',
            start: 'top 80%',
            end: 'bottom 50%',
            toggleActions: 'play none none none',
            once: true // 只执行一次
          }
        })

        // 添加产品卡片入场动画
        tl.from('.product-card', {
          y: 30,
          opacity: 0,
          duration: 0.3,
          stagger: {
            amount: 0.5,
            ease: 'power2.out'
          },
          clearProps: 'all' // 动画完成后清除属性
        })
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '@/assets/styles/variables.less';
@import '@/assets/styles/mixins.less';
.products-box{
.filter-section {
  width: 100%;
  position: relative;
  z-index: 10;
  .filter-btn {
    width: 100%;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 9px 0 rgba(147,147,147,0.5);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    font-size: 16px;
    color: #293340;
    cursor: pointer;
    i {
      font-size: 18px;
      color: #999;
    }
  }
}

  .products-section {
    padding: 12px 0 16px 0;
    .products-container {
      width: 100%;
      padding: 0;
    }
    .products-grid {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 16px;
    }
    .product-card {
      width: 100%;
      background: #fff;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      border: 1px solid #eee;
      padding: 7px 7px;
      cursor: pointer;
      .product-image {
        flex-shrink: 0;
        width: 98px;
        height: 98px;
        overflow: hidden;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
          width: 100%;
          height: auto;
          object-fit: cover;
        }
      }
      .product-content {
        padding: 0 0 0 12px;
        width: calc(100% - 98px - 12px);
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      .product-name {
        font-size: 14px;
        font-weight: 600;
        color: #293340;
        margin: 0 0 6px;
      }
      .product-chemical-name {
        font-size: 12px;
        color: #666;
        margin: 0 0 8px;
        width: 100%;
        word-break: break-word;
      }
      .product-info {
        display: flex;
        gap: 10px;
        font-size: 10px;
        color: #999;
        .product-cas, .product-sn {
          display: inline-block;

        }
      }
    }
    .no-data {
      text-align: center;
      padding: 32px 0;
      color: #999;
      font-size: 14px;
    }
    .pagination-section {
      background: none;
      padding: 0;
      .pagination-wrapper {
        width: 100%;
        padding: 0;
        font-size: 12px;
      }
    }
  }

// el-drawer内容样式
:deep(.el-drawer__body) {
  padding: 0 16px;
}
.filter-drawer-content {
  padding: 16px 0;
  /deep/ .el-drawer__header{
    margin-bottom: 0;
  }
  .category-group {
    margin-bottom: 18px;
  }
  .category-title {
    font-size: 16px;
    font-weight: 600;
    color: #293340;
    margin: 0 0 10px;
  }
  .category-list {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      padding: 10px 0;
      cursor: pointer;
      color: #666;
      border-bottom: 1px solid #f0f0f0;
      font-size: 15px;
      &.active {
        color: #19529f;
        font-weight: 600;
      }
    }
  }
}
}
</style>